import React, { useCallback, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from 'antd';
import styled from 'styled-components';
import ChatWindow from './components/ChatWindow';
import ChatInput from './components/ChatInput';
import { ChatContainer } from '../../styles/chat';
import { useChatState } from './hooks/useChatState';
import { useMessageHandling } from './hooks/useMessageHandling';
import { useSSEConnection } from './hooks/useSSEConnection';

// 错误提示容器
const ErrorContainer = styled.div`
    padding: 20px;
    margin: 10px;
`;

const Chat: React.FC = () => {
    const { thread_id, account_id } = useParams<{ thread_id: string, account_id: string }>();
    const prevThreadIdRef = useRef(thread_id);

    // 使用自定义hooks管理状态和逻辑
    const {
        messages,
        isLoading,
        error,
        currentPage,
        hasMore,
        suggestedQuestions,
        isLoadingSuggestions,
        isInitialized,
        setMessages,
        setError,
        setIsLoading,
        setSuggestedQuestions,
        setIsLoadingSuggestions,
        updatePagination,
        updateCurrentPage,
        markInitialized,
        resetInitialized
    } = useChatState();

    // SSE连接管理
    const { currentAssistantMessage, createConnection } = useSSEConnection();

    // 消息处理逻辑
    const { handleSendMessage, retryLoadHistory } = useMessageHandling({
        account_id: account_id!,
        thread_id: thread_id!,
        isLoading,
        currentAssistantMessage,
        createConnection,
        setMessages,
        setError,
        setIsLoading,
        setSuggestedQuestions,
        setIsLoadingSuggestions,
        updatePagination,
        isInitialized,
        markInitialized
    });

    // 加载更多消息
    const handleLoadMore = useCallback(async () => {
        if (currentPage > 1 && !isLoading) {
            const nextPage = currentPage - 1;
            updateCurrentPage(nextPage);
            await retryLoadHistory(nextPage, true);
        }
    }, [currentPage, isLoading, updateCurrentPage, retryLoadHistory]);

    // 监听 thread_id 变化，重置状态
    useEffect(() => {
        if (prevThreadIdRef.current !== thread_id && isInitialized) {
            resetInitialized();
            setMessages([]);
        }
        prevThreadIdRef.current = thread_id;
    }, [thread_id, isInitialized, resetInitialized, setMessages]);

    // 初始加载历史消息
    useEffect(() => {
        if (!isInitialized && thread_id && !isLoading) {
            retryLoadHistory(1, false);
        }
    }, [isInitialized, thread_id, isLoading, retryLoadHistory]);

    if (!thread_id || !account_id) {
        return (
            <ErrorContainer>
                <Alert message="缺少必要的参数" type="error" showIcon />
            </ErrorContainer>
        );
    }

    return (
        <ChatContainer>
            {error && (
                <ErrorContainer>
                    <Alert message={error} type="error" showIcon />
                </ErrorContainer>
            )}
            <ChatWindow
                messages={messages}
                onSendMessage={handleSendMessage}
                hasMore={hasMore}
                onLoadMore={handleLoadMore}
                suggestedQuestions={suggestedQuestions}
                lastMessageId={messages[messages.length - 1]?.id}
                isLoadingSuggestions={isLoadingSuggestions}
                isSSE={!!currentAssistantMessage}
            />
            <ChatInput 
                onSend={handleSendMessage} 
                disabled={isLoading} 
                threadId={thread_id}
                accountId={account_id}
            />
        </ChatContainer>
    );
};

export default React.memo(Chat); 